import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subject, catchError, map, of, switchMap, takeUntil } from 'rxjs';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { OrganizationLevel, OrganizationUnit, UnitSchedule } from '../models/unit.model';
import { UnitService } from '../services/unit.service';
import { UnitSchedulesDialogComponent, UnitSchedulesDialogData } from '../components/unit-schedules-dialog.component';
import { UnitEventsPageComponent } from './unit-events-page/unit-events-page.component';

const DAY_NAMES: Record<number, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

@Component({
  selector: 'app-unit-community-info-page',
  standalone: true,
  imports: [CommonModule, RouterLink, MatButtonModule, MatIconModule, MatProgressSpinnerModule, UnitEventsPageComponent],
  template: `
    <section class="community-info-page" *ngIf="!isLoading && unit; else loadingState">
      <article class="info-section address-section">
        <header class="section-heading">
          <h2><mat-icon>location_on</mat-icon> Dirección</h2>
          <a *ngIf="canManageAddress" mat-icon-button [routerLink]="['/units', unit.id, 'edit']" [queryParams]="{ section: 'address' }" aria-label="Editar dirección" title="Editar dirección"><mat-icon>edit</mat-icon></a>
        </header>
        <div class="section-body">
          <p *ngIf="addressText; else noAddress" class="address-text">{{ addressText }}</p>
          <ng-template #noAddress><p class="info-empty">Todavía no hay una dirección registrada.</p></ng-template>
        </div>
        <a *ngIf="addressText" class="section-action" [href]="mapsUrl" target="_blank" rel="noopener"><mat-icon>open_in_new</mat-icon> Ver en Google Maps</a>
      </article>

      <article class="info-section schedules-section">
        <header class="section-heading">
          <h2><mat-icon>calendar_today</mat-icon> Horarios</h2>
          <button *ngIf="canManageSchedules" mat-icon-button type="button" aria-label="Editar horarios" title="Editar horarios" (click)="editSchedules()">
            <mat-icon>edit</mat-icon>
          </button>
        </header>
        <div class="section-body">
          <p *ngIf="schedules.length === 0" class="info-empty">Todavía no hay horarios configurados.</p>
          <ul *ngIf="schedules.length > 0" class="schedule-list">
            <li *ngFor="let schedule of schedules">
              <div class="schedule-row"><strong>{{ getDayName(schedule.dayOfWeek) }}</strong><time>{{ schedule.startTime }}</time></div>
              <p *ngIf="schedule.notes" class="schedule-notes">{{ schedule.notes }}</p>
            </li>
          </ul>
        </div>
      </article>

      <article class="info-section contact-section">
        <header class="section-heading"><h2><mat-icon>contact_phone</mat-icon> Contacto</h2></header>
        <div class="section-body">
          <ng-container *ngIf="unit.leader; else noLeader">
            <p class="contact-name">
              <a *ngIf="unit.leaderId; else contactName" [routerLink]="['/people', unit.leaderId]">{{ unit.leader }}</a>
              <ng-template #contactName>{{ unit.leader }}</ng-template>
            </p>
            <p *ngIf="unit.leaderMinistryName || unit.leaderAppointmentName" class="contact-role">{{ unit.leaderMinistryName }}<ng-container *ngIf="unit.leaderMinistryName && unit.leaderAppointmentName">, </ng-container>{{ unit.leaderAppointmentName }}</p>
          </ng-container>
          <ng-template #noLeader><p class="info-empty">Todavía no hay una persona de contacto registrada.</p></ng-template>
        </div>
        <a *ngIf="unit.leaderPhone" class="section-action" [href]="'tel:' + unit.leaderPhone"><mat-icon>call</mat-icon><span>{{ unit.leaderPhone }}</span></a>
      </article>
      <section class="events-section" *ngIf="unit.organizationLevelId === 5 && (canViewEvents$ | async)" aria-label="Historial de eventos">
        <app-unit-events-page [unit]="unit"></app-unit-events-page>
      </section>
    </section>
    <ng-template #loadingState>
      <div class="loading-state" *ngIf="isLoading"><mat-spinner diameter="32"></mat-spinner></div>
      <div class="error-state" *ngIf="!isLoading && errorMessage">{{ errorMessage }}</div>
    </ng-template>
  `,
  styles: [`
    :host { display: block; min-height: 100%; font-family: 'Inter', 'Helvetica Neue', sans-serif; }
    .community-info-page { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 270px), 1fr)); gap: 16px; width: 100%; max-width: 888px; margin-inline: auto; padding: 18px 4px 4px; color: #255581; }
    .events-section { grid-column: 1 / -1; width: 100%; max-width: 880px; min-width: 0; margin-top: 8px; }
    :host ::ng-deep .events-section app-data-table .table-container app-empty-state { height: auto; min-height: 0; }
    :host ::ng-deep .events-section .empty-state-container { flex-direction: row; justify-content: flex-start; min-height: 0; gap: 10px; padding: 20px; text-align: left; }
    :host ::ng-deep .events-section .empty-state-icon { flex-shrink: 0; width: 24px; height: 24px; font-size: 24px; line-height: 24px; }
    :host ::ng-deep .events-section .empty-state-title { color: #66788a; font-size: 13px; font-weight: 400; line-height: 1.5; }
    :host ::ng-deep .events-section .empty-state-description,
    :host ::ng-deep .events-section .empty-state-content:empty { display: none; }
    .info-section { --accent: #93b5d8; --icon: #4176a6; --tint: #eaf1f8; display: flex; flex-direction: column; min-width: 0; overflow: hidden; border: 1px solid #dfe8f1; border-radius: 20px; background: #fff; box-shadow: 0 2px 4px #244b7314; }
    .info-section::before { content: ''; height: 5px; background: var(--accent); }
    .schedules-section { --accent: #bcd9ff; --icon: #2878e9; --tint: #eff6ff; }
    .contact-section { --accent: #accfb8; --icon: #008574; --tint: #ebfbf7; }
    .section-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 64px; padding: 12px 16px; }
    .section-heading h2 { display: flex; align-items: center; gap: 9px; min-width: 0; margin: 0; font-size: 15px; font-weight: 600; }
    .section-heading h2 mat-icon { box-sizing: content-box; flex-shrink: 0; width: 20px; height: 20px; padding: 6px; border-radius: 50%; background: var(--tint); color: var(--icon); font-size: 20px; line-height: 20px; }
    .section-heading button { flex-shrink: 0; color: #4176a6; }
    .section-heading a { flex-shrink: 0; color: #4176a6; }
    .section-body { flex: 1; padding: 0 16px 18px; font-size: 14px; line-height: 1.6; overflow-wrap: anywhere; }
    .section-body p { margin: 0; }
    .section-body .info-empty { padding: 8px 0; color: #66788a; font-size: 13px; }
    .address-text { white-space: pre-line; }
    .contact-name { font-size: 15px; font-weight: 600; }
    .contact-name a { color: #1668c7; text-decoration: none; }
    .contact-name a:hover { text-decoration: underline; }
    .contact-name a:focus-visible { outline: 2px solid #1675e8; outline-offset: 3px; border-radius: 2px; }
    .section-body .contact-role { margin-top: 4px; color: #66788a; font-size: 13px; }
    .schedule-list { display: grid; gap: 8px; margin: 0; padding: 0; list-style: none; }
    .schedule-list li { padding: 8px 10px; border: 1px solid #edf1f6; border-radius: 10px; background: #f8fafc; }
    .schedule-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
    .schedule-row strong { font-weight: 500; }
    .schedule-row time { flex-shrink: 0; color: #255581; font-size: 16px; font-weight: 600; font-variant-numeric: tabular-nums; }
    .section-body .schedule-notes { margin-top: 4px; color: #66788a; font-size: 12px; }
    .section-action { display: flex; align-items: center; gap: 8px; min-height: 44px; padding: 10px 16px; border-top: 1px solid #edf1f6; color: #1668c7; font-size: 13px; font-weight: 500; text-decoration: none; overflow-wrap: anywhere; }
    .section-action mat-icon { flex-shrink: 0; width: 18px; height: 18px; font-size: 18px; line-height: 18px; }
    .section-action:hover { background: #f0f7ff; }
    .section-action:focus-visible { outline: 2px solid #1675e8; outline-offset: -2px; }
    .loading-state, .error-state { display: flex; justify-content: center; padding: 32px; }
    .error-state { color: #b42318; }
  `],
})
export class UnitCommunityInfoPageComponent implements OnInit, OnDestroy {
  unit: OrganizationUnit | null = null;
  schedules: UnitSchedule[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  readonly canManageSchedules: boolean;
  readonly canViewEvents$: Observable<boolean>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly unitService: UnitService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly userFullInfoService: UserFullInfoService,
  ) {
    this.canManageSchedules = this.userFullInfoService.canManageMemberDataSnapshot();
    this.canViewEvents$ = this.userFullInfoService.canViewRestrictedData$;
  }

  get addressText(): string {
    const detail = this.unit?.detail;
    return detail ? [detail.addressLine, detail.city, detail.department, detail.province, detail.countryName].filter(Boolean).join(', ') : '';
  }

  get canManageAddress(): boolean {
    const user = this.userFullInfoService.getUserInfoSnapshot();
    return !!this.unit && !!(user?.isAdmin ||
      (this.userFullInfoService.hasPermission('ManageUnits', user) &&
        user?.manageableUnitIds.some(id => id.toLowerCase() === this.unit!.id.toLowerCase())));
  }

  get mapsUrl(): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.addressText)}`;
  }

  ngOnInit(): void {
    const unitId = this.route.pathFromRoot.reduce((id, route) => id ?? route.snapshot.paramMap.get('unit-id'), null as string | null);
    if (!unitId) {
      this.isLoading = false;
      this.errorMessage = 'No se pudo abrir la comunidad solicitada.';
      return;
    }
    this.unitService.getUnit(unitId).pipe(
      switchMap(unit => {
        if (unit.organizationLevelId !== OrganizationLevel.Comunidad) {
          return of({ unit, schedules: [] as UnitSchedule[] });
        }
        return this.unitService.getSchedules(unitId).pipe(
          catchError(() => of([] as UnitSchedule[])),
          map(schedules => ({ unit, schedules })),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: result => {
        this.unit = result.unit;
        this.schedules = result.schedules;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'No se pudieron cargar los datos de la comunidad.';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDayName(dayOfWeek: number): string {
    return DAY_NAMES[dayOfWeek] ?? '';
  }

  editSchedules(): void {
    if (!this.unit || !this.canManageSchedules) return;
    this.dialog.open<UnitSchedulesDialogComponent, UnitSchedulesDialogData, UnitSchedule[]>(
      UnitSchedulesDialogComponent,
      modalDialogConfig({
        data: { unitId: this.unit.id, schedules: this.schedules },
        size: 'md',
        layout: 'form',
        disableClose: true,
      }),
    ).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(schedules => {
      if (Array.isArray(schedules)) {
        this.schedules = schedules;
        this.cdr.markForCheck();
      }
    });
  }
}
